import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles, Video, Wand2, Clock } from 'lucide-react';
import { cn } from '@/utils/cn';

// Timeout constants
const MAX_WAIT_TIME_MS = 70000; // 70 seconds max wait before graceful fallback
const SLOW_WARNING_TIME_MS = 45000; // Show "taking longer" message after 45 seconds

interface PreviewLoadingScreenProps {
  isOpen: boolean;
  previewImageUrl?: string;
  onComplete: () => void;
}

export function PreviewLoadingScreen({ 
  isOpen, 
  previewImageUrl,
  onComplete 
}: PreviewLoadingScreenProps) {
  const { t } = useTranslation();
  const [progress, setProgress] = useState(0);
  const [stage, setStage] = useState(0);
  const [showImage, setShowImage] = useState(false);
  const [isTakingLong, setIsTakingLong] = useState(false);
  const onCompleteRef = useRef(onComplete);
  
  // Keep onComplete ref updated
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);
  
  const stages = [
    { icon: Wand2, text: t('preview.analyzingPrompt') || 'Analyzing your prompt...' },
    { icon: Video, text: '...' },
    { icon: isTakingLong ? Clock : Sparkles, text: isTakingLong 
      ? '...' 
      : '...' },
  ];

  // Main animation and timeout effect
  useEffect(() => {
    if (!isOpen) {
      setProgress(0);
      setStage(0);
      setShowImage(false);
      setIsTakingLong(false);
      return;
    }

    // Animate progress over ~10 seconds total to reach 90%
    // - 0-70%: ~6 seconds (fast phase)
    // - 70-90%: ~4 seconds (slow phase)
    // Cap at 90% if no image yet (waits for API response)
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        // If we already have the image, let the other useEffect handle completion
        if (previewImageUrl) {
          return prev;
        }
        // Cap at 90% to wait for API response
        if (prev >= 90) {
          return 90;
        }
        // Slow down after 70% (0.25 per 50ms = 20% in ~4 seconds)
        if (prev > 70) {
          return prev + 0.25;
        }
        // Normal speed until 70% (0.6 per 50ms = 70% in ~6 seconds)
        return prev + 0.6;
      });
    }, 50);

    // Change stages over 10 seconds total:
    // Stage 0 (Analyzing): 0-3s
    // Stage 1 (Creating preview): 3-7s  
    // Stage 2 (Almost ready): 7-10s
    const stageTimeout1 = setTimeout(() => setStage(1), 3000);
    const stageTimeout2 = setTimeout(() => setStage(2), 7000);
    
    // Show "taking longer than expected" message after 45s
    const slowWarningTimeout = setTimeout(() => {
      setIsTakingLong(true);
    }, SLOW_WARNING_TIME_MS);
    
    // Max wait timeout - gracefully complete if preview doesn't arrive in time
    // This prevents the user from being stuck on the loading screen forever
    const maxWaitTimeout = setTimeout(() => {
      console.log('⏱️ Preview timeout reached, navigating to files...');
      onCompleteRef.current();
    }, MAX_WAIT_TIME_MS);

    return () => {
      clearInterval(progressInterval);
      clearTimeout(stageTimeout1);
      clearTimeout(stageTimeout2);
      clearTimeout(slowWarningTimeout);
      clearTimeout(maxWaitTimeout);
    };
  }, [isOpen, previewImageUrl]);

  // When we have the preview image, complete the animation
  useEffect(() => {
    if (previewImageUrl && isOpen) {
      // Jump to 100% and show the image
      setProgress(100);
      setStage(2); // "Almost ready!" stage
      setShowImage(true);
      setIsTakingLong(false);
      
      // Show the image briefly, then complete
      const timer = setTimeout(() => {
        onCompleteRef.current();
      }, 1500);
      
      return () => clearTimeout(timer);
    }
  }, [previewImageUrl, isOpen]);

  if (!isOpen) return null;

  const CurrentIcon = stages[stage].icon;
  const circumference = 2 * Math.PI * 45; // radius = 45
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/95 backdrop-blur-md">
      <div className="flex flex-col items-center gap-8 p-8 max-w-sm mx-auto text-center">
        {/* Circular Progress */}
        <div className="relative w-32 h-32">
          {/* Background circle */}
          <svg className="w-full h-full -rotate-90">
            <circle
              cx="64"
              cy="64"
              r="45"
              fill="none"
              stroke="currentColor"
              strokeWidth="6"
              className="text-surface-light"
            />
            {/* Progress circle */}
            <circle
              cx="64"
              cy="64"
              r="45"
              fill="none"
              stroke="url(#gradient)"
              strokeWidth="6"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              className="transition-all duration-300 ease-out"
            />
            <defs>
              <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#f59e0b" />
                <stop offset="100%" stopColor="#ec4899" />
              </linearGradient>
            </defs>
          </svg>
          
          {/* Center content */}
          <div className="absolute inset-0 flex items-center justify-center">
            {showImage && previewImageUrl ? (
              <div className="w-20 h-20 rounded-full overflow-hidden animate-scale-in">
                <img 
                  src={previewImageUrl} 
                  alt="Preview" 
                  className="w-full h-full object-cover"
                  style={{ filter: 'blur(3px)' }}
                />
              </div>
            ) : (
              <div className={cn(
                "w-12 h-12 rounded-full flex items-center justify-center",
                "bg-gradient-to-br from-amber-500/20 to-pink-500/20",
                "animate-pulse"
              )}>
                <CurrentIcon className="w-6 h-6 text-amber-500 animate-bounce" />
              </div>
            )}
          </div>
        </div>

        {/* Stage text */}
        <div className="space-y-3">
          <h2 className="text-xl font-semibold text-text-primary animate-fade-in">
            {showImage 
              ? ('')
              : stages[stage].text
            }
          </h2>
          <p className="text-sm text-text-secondary">
            {showImage 
              ? ('')
              : ('')
            }
          </p>
        </div>

        {/* Progress percentage */}
        <div className="flex items-center gap-2">
          <span className="text-2xl font-bold bg-gradient-to-r from-amber-500 to-pink-500 bg-clip-text text-transparent">
            {Math.round(progress)}%
          </span>
        </div>

        {/* Animated dots */}
        {!showImage && (
          <div className="flex gap-2">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className={cn(
                  "w-2 h-2 rounded-full bg-amber-500",
                  "animate-bounce"
                )}
                style={{ animationDelay: `${i * 150}ms` }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

